import { useEffect, useState } from "react";
import { Star, User, Gamepad2, Shield, ShieldCheck, Heart, Check, Crown } from "lucide-react";
import { api } from "../lib/api";

// Staff role visual treatment for forum posts/comments so admins & mods stand out.
export const roleAccent = (role) => {
  if (role === "admin") return { key: "admin", label: "Admin", color: "#D4A94A",
    border: "border-[#D4A94A]/60", bg: "bg-gradient-to-r from-[#D4A94A]/[0.12] via-[#D4A94A]/[0.04] to-transparent", icon: ShieldCheck };
  if (role === "moderator") return { key: "moderator", label: "Mod", color: "#2EC4B6",
    border: "border-[#2EC4B6]/60", bg: "bg-gradient-to-r from-[#2EC4B6]/[0.12] via-[#2EC4B6]/[0.04] to-transparent", icon: Shield };
  return null;
};

export const RoleBadge = ({ role, className = "" }) => {
  const a = roleAccent(role);
  if (!a) return null;
  const Icon = a.icon;
  return (
    <span data-testid={`role-badge-${a.key}`} title={`${a.label} — OCE DOGS staff`}
      className={`inline-flex items-center gap-1 font-stencil font-black uppercase text-[9px] px-1.5 py-0.5 tactical-frame-sm ${className}`}
      style={{ background: a.color, color: "#0B0C0E" }}>
      <Icon className="w-2.5 h-2.5" /> {a.label}
    </span>
  );
};

export const RARITY = {
  common: { hex: "#9D9D9D", label: "COMMON" },
  uncommon: { hex: "#2ECC71", label: "UNCOMMON" },
  rare: { hex: "#3498DB", label: "RARE" },
  epic: { hex: "#9B59B6", label: "EPIC" },
  legendary: { hex: "#D4A94A", label: "LEGENDARY" },
};

export const FACTIONS = {
  VALKYRA: { id: "VALKYRA", name: "Valkyra", color: "#E4443A", image: "/factions/valkyra.webp" },
  LONESTAR: { id: "LONESTAR", name: "Lonestar", color: "#3498DB", image: "/factions/lonestar.webp" },
  MANTICORE: { id: "MANTICORE", name: "Manticore", color: "#2ECC71", image: "/factions/manticore.webp" },
};

export const FactionCrest = ({ faction, size = 26, className = "" }) => {
  const f = faction && FACTIONS[String(faction).toUpperCase()];
  if (!f) return null;
  return (
    <span
      data-testid={`faction-crest-${f.id}`}
      title={`${f.name} faction`}
      className={`inline-flex items-center justify-center shrink-0 tactical-frame-sm overflow-hidden border ${className}`}
      style={{ width: size, height: size, borderColor: `${f.color}66`, background: "#0B0C0E", boxShadow: `0 0 8px ${f.color}44` }}
    >
      <img src={f.image} alt={`${f.name} emblem`} loading="lazy" decoding="async" className="w-full h-full object-contain p-[2px]" />
    </span>
  );
};

// Live website-presence indicator. Renders nothing unless the operator is online right now.
export const OnlineDot = ({ online, size = 9, showLabel = false, className = "" }) => {
  if (!online) return null;
  return (
    <span data-testid="online-dot" title="Online now" className={`inline-flex items-center gap-1.5 shrink-0 ${className}`}>
      <span className="relative inline-flex" style={{ width: size, height: size }}>
        <span className="absolute inline-flex w-full h-full rounded-full bg-[#2ECC71] opacity-70 animate-ping" />
        <span className="relative inline-flex rounded-full w-full h-full bg-[#2ECC71]" style={{ boxShadow: "0 0 6px #2ECC71" }} />
      </span>
      {showLabel && <span className="font-mono text-[10px] uppercase tracking-wider text-[#2ECC71]">Online</span>}
    </span>
  );
};

export const timeAgo = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return null;
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d < 30 ? `${d}d ago` : `${Math.floor(d / 30)}mo ago`;
};

// Unified presence badge. Priority: in-game (amber) > online on site (green) > last seen (muted).
// mode="full" shows labels; mode="compact" shows just a dot/icon (for tight rows).
export const PresenceBadge = ({ inServer, online, lastSeen, mode = "full", className = "" }) => {
  if (inServer) {
    if (mode === "compact") {
      return (
        <span data-testid="presence-ingame" title="In the game right now" className={`inline-flex shrink-0 ${className}`}>
          <Gamepad2 className="w-3.5 h-3.5 text-[#D4A94A]" style={{ filter: "drop-shadow(0 0 4px #D4A94A88)" }} />
        </span>
      );
    }
    return (
      <span data-testid="presence-ingame" title="In the game right now" className={`inline-flex items-center gap-1.5 shrink-0 font-mono text-[10px] uppercase tracking-wider text-[#D4A94A] border border-[#D4A94A]/40 bg-[#D4A94A]/10 px-2 py-0.5 tactical-frame-sm ${className}`}>
        <Gamepad2 className="w-3 h-3" /> In Game
      </span>
    );
  }
  if (online) return <OnlineDot online showLabel={mode === "full"} className={className} />;
  const ago = timeAgo(lastSeen);
  if (ago && mode === "full") {
    return <span data-testid="presence-lastseen" className={`inline-flex items-center gap-1.5 shrink-0 font-mono text-[10px] uppercase tracking-wider text-[#64748B] ${className}`}>
      <span className="w-2 h-2 rounded-full bg-[#64748B] inline-block" /> Last seen {ago}
    </span>;
  }
  return null;
};


let catalogPromise = null;
export const useCatalog = () => {
  const [byId, setById] = useState({});
  useEffect(() => {
    if (!catalogPromise) catalogPromise = api.get("/market/catalog").then((r) => r.data).catch(() => []);
    catalogPromise.then((items) => setById(Object.fromEntries(items.map((i) => [i.id, i]))));
  }, []);
  return byId;
};

export const RarityBadge = ({ rarity, className = "" }) => {
  const r = RARITY[rarity] || RARITY.common;
  return (
    <span className={`text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 tracking-wider ${className}`} style={{ background: r.hex, color: "#0B0C0E" }}>
      {r.label}
    </span>
  );
};

export const FrameRing = ({ color, styleName, animated }) => {
  if (!styleName || styleName === "none") return null;
  const cls = animated ? "frx-anim" : `frx-${styleName}`;
  return <span className={`frx-ring ${cls}`} style={{ "--fc": color }} aria-hidden="true" />;
};

export const DogTag = ({ frameId, avatarUrl, callsign, size = 36, isSupporter = false, isPatron = false, isPartner = false, vip = false, alt = "", className = "" }) => {
  const catalog = useCatalog();
  const frame = frameId ? catalog[frameId] : null;
  const color = frame?.color || "#2A2E3D";
  const styleName = frame ? (frame.style || "solid") : "none";
  const src = avatarUrl || `https://api.dicebear.com/9.x/bottts-neutral/svg?seed=${callsign || "op"}`;
  const pad = frame ? Math.max(3, Math.round(size * 0.07)) : 0;
  return (
    <div className={`relative shrink-0 ${className}`} style={{ width: size, height: size }} data-testid="dogtag" title={frame ? frame.name : undefined}>
      <FrameRing color={color} styleName={styleName} animated={frame?.animated} />
      <div className="absolute tactical-frame-sm overflow-hidden bg-[#0B0C0E] border border-[#2A2E3D] z-[2]" style={{ inset: pad }}>
        {src ? <img src={src} alt={alt} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <User className="w-1/2 h-1/2 text-[#94A3B8]" />}
      </div>
      {isPartner && (
        <span className="partner-avatar-ring absolute tactical-frame-sm z-[3] pointer-events-none" style={{ inset: -2 }} aria-hidden data-testid="dogtag-partner-ring" />
      )}
      {isPartner && (
        <span title="OCE DOGS Partner" data-testid="dogtag-partner" className="partner-box absolute -top-1 -right-1 rounded-[5px] flex items-center justify-center z-[5]" style={{ width: Math.max(14, Math.round(size * 0.32)), height: Math.max(14, Math.round(size * 0.32)) }}>
          <Check strokeWidth={4} className="text-white" style={{ width: Math.max(9, Math.round(size * 0.2)), height: Math.max(9, Math.round(size * 0.2)) }} />
        </span>
      )}
      {vip && (
        <span className="vip-avatar-ring absolute tactical-frame-sm z-[3] pointer-events-none" style={{ inset: -2 }} aria-hidden data-testid="dogtag-vip-ring" />
      )}
      {vip && (
        <span title="VIP member" data-testid="dogtag-vip" className="vip-crown-box absolute -top-1 -left-1 rounded-[5px] flex items-center justify-center z-[5]" style={{ width: Math.max(14, Math.round(size * 0.32)), height: Math.max(14, Math.round(size * 0.32)) }}>
          <Crown strokeWidth={2.5} className="text-white" style={{ width: Math.max(9, Math.round(size * 0.2)), height: Math.max(9, Math.round(size * 0.2)) }} />
        </span>
      )}
      {isSupporter && (
        <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[#0B0C0E] flex items-center justify-center border border-[#D4A94A] z-[4]">
          <Star className="w-2.5 h-2.5 text-[#D4A94A] fill-[#D4A94A]" />
        </span>
      )}
      {isPatron && (
        <span title="Patreon supporter" data-testid="dogtag-patron" className="absolute -bottom-1 -left-1 w-4 h-4 rounded-full bg-[#0B0C0E] flex items-center justify-center border border-[#F96854] z-[4]">
          <Heart className="w-2.5 h-2.5 text-[#F96854] fill-[#F96854]" />
        </span>
      )}
    </div>
  );
};

export const BannerFX = ({ fx, color, className = "" }) => (
  <>
    <span className={`abx-layer abx-${fx} ${className}`} style={{ "--abx": color }} />
    <span className="abx-layer abx-sheen" />
    <span className="abx-layer abx-glow" style={{ "--abx": color }} />
  </>
);

export const BannerStrip = ({ bannerId, compact = false, className = "" }) => {
  const catalog = useCatalog();
  const banner = bannerId ? catalog[bannerId] : null;
  if (!banner) return null;
  const bg = banner.image
    ? { backgroundImage: `linear-gradient(90deg, rgba(11,12,14,0.15), rgba(11,12,14,0.35)), url(${banner.image})`, backgroundSize: "cover", backgroundPosition: "center", borderColor: `${banner.color}88`, color: "#fff", textShadow: "0 1px 3px rgba(0,0,0,0.8)" }
    : { background: `linear-gradient(90deg, ${banner.color}33, ${banner.color}0D)`, borderColor: `${banner.color}88`, color: "#F1F5F9" };
  return (
    <span
      data-testid="banner-strip"
      className={`inline-flex items-center gap-1.5 font-mono uppercase tracking-wider ${compact ? "text-[9px] px-1.5 py-0.5" : "text-[10px] px-2 py-1"} tactical-frame-sm border ${banner.animated ? "abx" : ""} ${className}`}
      style={bg}
      title={banner.name}
    >
      {banner.animated && <BannerFX fx={banner.fx} color={banner.color} />}
      <span className="w-1.5 h-3 relative z-[5]" style={{ background: banner.color }} />
      <span className="relative z-[5]">{banner.name.replace(/ Banner$/i, "")}</span>
    </span>
  );
};

export const CosmeticPreview = ({ item, size = 56 }) => {
  if (!item) return null;
  if (item.kind === "frame") {
    return (
      <div className="relative mx-auto" style={{ width: size, height: size }}>
        <FrameRing color={item.color} styleName={item.style || "solid"} animated={item.animated} />
        <div className="absolute z-[2] bg-[#0B0C0E] tactical-frame-sm flex items-center justify-center overflow-hidden" style={{ inset: Math.max(3, Math.round(size * 0.07)) }}>
          <Star className="w-1/2 h-1/2" style={{ color: item.color }} />
        </div>
      </div>
    );
  }
  if (item.kind === "banner") {
    return (
      <div className={`tactical-frame-sm border border-[#2A2E3D] flex items-center justify-center overflow-hidden relative ${item.animated ? "abx" : ""}`} style={{ width: size * 2, height: size / 1.6, background: item.image ? `url(${item.image})` : `linear-gradient(135deg, ${item.color}, ${item.color}33)`, backgroundSize: "cover", backgroundPosition: "center" }}>
        {item.animated && <BannerFX fx={item.fx} color={item.color} />}
        {!item.image && <span className="text-[9px] font-mono uppercase text-white/85 relative z-[5]">{item.name.split(" ").slice(0, 2).join(" ")}</span>}
      </div>
    );
  }
  return <div className="font-heading font-black text-2xl text-[#D4A94A]">+{item.amount} XP</div>;
};
